import { useEffect, useState } from 'react'
import { Key } from './ui/Key'
import './style/game.css'
import './style/lobby.css'

/** Ours, and absurd on purpose: something to read while the street is put up. */
const LINES = [
  'Teaching the kettle to whistle in tune',
  'Counting the garden gnomes, twice',
  'Persuading the hedge to grow square',
  'Alphabetising the spice rack',
  'Negotiating with the thermostat',
  'Ironing the curtains flat',
  'Winding the hall clock back a minute',
  'Hiding the good biscuits',
  'Fluffing the sofa cushions',
  'Mowing the lawns in stripes',
]

const EVERY_MILLISECONDS = 2600

/** What a first load waits on, in the order it meets them: the houses, the world's code, then the village drawn. */
export type Waiting = 'houses' | 'world' | 'village'
const WAITING: Record<Waiting, string> = {
  houses: 'Finding your houses',
  world: 'Unpacking the world',
  village: 'Putting up the village',
}
const STAGES = Object.keys(WAITING) as Waiting[]

/** The line the page opened on, which every loading screen after counts on from, so one shown in another's place keeps its line. */
const FIRST_LINE = Math.floor(Math.random() * LINES.length)
const linesSinceOpening = () => Math.floor(performance.now() / EVERY_MILLISECONDS)

/**
 * The game's loading screen, from the first paint until the first village stands, saying what it waits on. Its bar
 * moves in CSS alone, so it keeps moving through a long task, and every screen's bar keeps the page's time, so a
 * screen shown in another's place carries on where that one was. Its root declares the game's tokens again, so the
 * game's own reduced-motion option is passed in; before the game's options load, the system's setting alone stills it.
 */
export function Loading({ waiting = 'houses', reducedMotion = false }: { waiting?: Waiting; reducedMotion?: boolean }) {
  const [lines, setLines] = useState(linesSinceOpening)
  useEffect(() => {
    const timer = window.setTimeout(() => setLines((shown) => Math.max(shown + 1, linesSinceOpening())), EVERY_MILLISECONDS - (performance.now() % EVERY_MILLISECONDS))
    return () => window.clearTimeout(timer)
  }, [lines])
  const [since] = useState(() => `${-performance.now()}ms`)
  const reached = STAGES.indexOf(waiting)
  return (
    <div className="game game-loading" role="status" aria-label="Loading Stageville" data-reduced-motion={reducedMotion || undefined}>
      <p className="game-loading-title">Stageville</p>
      <ol className="game-loading-stages" aria-hidden="true">
        {STAGES.map((stage, index) => (
          <li key={stage} data-done={index < reached || undefined} data-now={index === reached || undefined}>
            <span style={{ animationDelay: since }} />
          </li>
        ))}
      </ol>
      <div className="game-loading-words">
        <p className="game-loading-waiting">{WAITING[waiting]}</p>
        <p className="game-loading-line" aria-hidden="true">{LINES[(FIRST_LINE + lines) % LINES.length]}…</p>
      </div>
    </div>
  )
}

/** The server did not answer. Not a sign-in problem, so it says so and offers another go. */
export function Unreachable({ detail, onRetry }: { detail: string; onRetry: () => void }) {
  return (
    <div role="alert" className="game game-loading">
      <p className="game-loading-title">Stageville is not answering.</p>
      <p className="game-loading-line">{detail}</p>
      <Key variant="primary" onClick={onRetry}>Try again</Key>
    </div>
  )
}
